// ============================================
// GRAPHIQUES ET INDICATEURS
// ============================================
// Rendu SVG maison, sans librairie de charting. Sans état ni contexte.
// Extraits de AdminV2.tsx.

import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card } from '../ui';

export const KPICard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: number; isPositive: boolean };
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'purple' | 'indigo';
  onClick?: () => void;
  subtitle?: string;
}> = ({ title, value, icon, trend, color = 'blue', onClick, subtitle }) => {
  // Le chiffre porte l'information ; la couleur ne sert qu'à signaler un état
  // qui demande l'attention (rouge, ambre). Le reste reste en encre.
  const accent = {
    blue: 'text-encre',
    green: 'text-sapin-800',
    yellow: 'text-[#7A4A00]',
    red: 'text-[#8A1C16]',
    purple: 'text-petrol-700',
    indigo: 'text-petrol-700',
  };
  return (
    // Sur téléphone, deux indicateurs par ligne : marges et chiffre un cran plus petits.
    <Card className={`p-5 max-sm:p-4 ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick}>
      <div className="flex items-center justify-between gap-3 max-sm:gap-2">
        <p className="text-sm font-semibold text-graphite min-w-0">{title}</p>
        <span className="text-graphite/70 [&>svg]:w-4 [&>svg]:h-4 flex-shrink-0">{icon}</span>
      </div>
      <div className="mt-3 flex items-baseline gap-3">
        <p className={`tnum text-3xl max-sm:text-2xl font-extrabold tracking-tight leading-none ${accent[color]}`}>{value}</p>
        {trend && (
          <span className={`tnum inline-flex items-center text-xs font-semibold ${trend.isPositive ? 'text-sapin-700' : 'text-[#8A1C16]'}`}>
            {trend.isPositive ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
            {Math.abs(trend.value)}%
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-graphite mt-1.5">{subtitle}</p>}
    </Card>
  );
};

// ============================================
// CHART COMPONENTS - Data Visualization
// ============================================

// Donut Chart Component
export const DonutChart: React.FC<{
  data: { label: string; value: number; color: string }[];
  size?: number;
  thickness?: number;
  showLegend?: boolean;
  centerLabel?: string;
  centerValue?: string | number;
}> = ({ data, size = 180, thickness = 35, showLegend = true, centerLabel, centerValue }) => {
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const radius = (size - thickness) / 2;
  const circumference = 2 * Math.PI * radius;
  let currentOffset = 0;

  return (
    <div className="flex flex-col items-center gap-4">
      <div className="relative max-w-full aspect-square" style={{ width: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="transform -rotate-90 w-full h-full">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#EEF3FA" strokeWidth={thickness} />
          {data.map((segment, i) => {
            const percentage = total > 0 ? segment.value / total : 0;
            const strokeLength = percentage * circumference;
            const offset = currentOffset;
            currentOffset += strokeLength;
            return (
              <circle
                key={i}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={segment.color}
                strokeWidth={thickness}
                strokeDasharray={`${strokeLength} ${circumference - strokeLength}`}
                strokeDashoffset={-offset}
                className="transition-all duration-700 ease-out"
              />
            );
          })}
        </svg>
        {(centerLabel || centerValue !== undefined) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            {centerValue !== undefined && <span className="text-2xl font-extrabold tracking-tight text-encre">{centerValue}</span>}
            {centerLabel && <span className="text-xs text-graphite">{centerLabel}</span>}
          </div>
        )}
      </div>
      {showLegend && (
        <div className="flex flex-wrap justify-center gap-2">
          {data.filter(d => d.value > 0).map((segment, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: segment.color }} />
              <span className="text-graphite">{segment.label}</span>
              <span className="font-semibold">{segment.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Area Line Chart Component
export const AreaLineChart: React.FC<{
  data: { label: string; value: number }[];
  width?: number;
  height?: number;
  color?: string;
  gradientId?: string;
}> = ({ data, width = 350, height = 180, color = '#2563EB', gradientId = 'areaGradient' }) => {
  if (data.length < 2) return <div className="text-graphite/70 text-sm text-center py-8">Données insuffisantes</div>;

  const padding = { top: 20, right: 10, bottom: 35, left: 35 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const maxVal = Math.max(...data.map(d => d.value), 1);

  const getX = (i: number) => padding.left + (i / (data.length - 1)) * chartW;
  const getY = (v: number) => padding.top + chartH - (v / maxVal) * chartH;

  const linePath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(d.value)}`).join(' ');
  const areaPath = `${linePath} L ${getX(data.length - 1)} ${padding.top + chartH} L ${padding.left} ${padding.top + chartH} Z`;

  return (
    // viewBox + max-w-full : le graphique se réduit dans un conteneur plus étroit que lui (téléphone).
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible max-w-full h-auto">
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity={0.3} />
          <stop offset="100%" stopColor={color} stopOpacity={0.02} />
        </linearGradient>
      </defs>
      {[0, 0.5, 1].map((r, i) => (
        <g key={i}>
          <line x1={padding.left} y1={getY(maxVal * r)} x2={width - padding.right} y2={getY(maxVal * r)} stroke="#DCE4EF" strokeDasharray={r === 0 ? "0" : "3,3"} />
          <text x={padding.left - 5} y={getY(maxVal * r) + 4} textAnchor="end" className="text-[10px] fill-gray-400">{Math.round(maxVal * r)}</text>
        </g>
      ))}
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path d={linePath} fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={getX(i)} cy={getY(d.value)} r={4} fill="white" stroke={color} strokeWidth={2} className="transition-all" />
          <text x={getX(i)} y={height - 8} textAnchor="middle" className="text-[9px] fill-gray-500">{d.label}</text>
        </g>
      ))}
    </svg>
  );
};

// Vertical Bar Chart Component
export const VerticalBarChart: React.FC<{
  data: { label: string; value: number; color: string }[];
  width?: number;
  height?: number;
}> = ({ data, width = 350, height = 200 }) => {
  if (data.length === 0) return null;
  const padding = { top: 25, right: 10, bottom: 40, left: 35 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const barW = Math.min(40, (chartW / data.length) * 0.7);
  const gap = (chartW - barW * data.length) / (data.length + 1);

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="max-w-full h-auto">
      {[0, 0.5, 1].map((r, i) => (
        <g key={i}>
          <line x1={padding.left} y1={padding.top + chartH * (1 - r)} x2={width - padding.right} y2={padding.top + chartH * (1 - r)} stroke="#DCE4EF" strokeDasharray={r === 0 ? "0" : "3,3"} />
          <text x={padding.left - 5} y={padding.top + chartH * (1 - r) + 4} textAnchor="end" className="text-[10px] fill-gray-400">{Math.round(maxVal * r)}</text>
        </g>
      ))}
      {data.map((d, i) => {
        const x = padding.left + gap + i * (barW + gap);
        const barH = (d.value / maxVal) * chartH;
        const y = padding.top + chartH - barH;
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} rx={4} fill={d.color} className="transition-all duration-500" />
            <rect x={x} y={y} width={barW / 2} height={barH} rx={4} fill="rgba(255,255,255,0.2)" />
            {d.value > 0 && <text x={x + barW / 2} y={y - 6} textAnchor="middle" className="text-[10px] font-semibold fill-gray-700">{d.value}</text>}
            <text x={x + barW / 2} y={height - 10} textAnchor="middle" className="text-[9px] fill-gray-500">{d.label.slice(0, 8)}</text>
          </g>
        );
      })}
    </svg>
  );
};

// Horizontal Bar Chart
export const HorizontalBarChart: React.FC<{
  data: { label: string; value: number; color: string; percent?: number }[];
  showPercent?: boolean;
}> = ({ data, showPercent = true }) => {
  const maxVal = Math.max(...data.map(d => d.value), 1);
  return (
    <div className="space-y-3">
      {data.map((d, i) => (
        <div key={i}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-sm text-encre">{d.label}</span>
            <span className="text-sm font-semibold text-encre">
              {d.value} {showPercent && d.percent !== undefined && <span className="text-graphite/70 font-normal">({d.percent}%)</span>}
            </span>
          </div>
          <div className="h-2.5 bg-brume rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(d.value / maxVal) * 100}%`, backgroundColor: d.color }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Mini Sparkline
export const Sparkline: React.FC<{ data: number[]; color?: string; width?: number; height?: number }> = ({ data, color = '#2563EB', width = 80, height = 24 }) => {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const getX = (i: number) => (i / (data.length - 1)) * width;
  const getY = (v: number) => height - (v / max) * height;
  const path = data.map((v, i) => `${i === 0 ? 'M' : 'L'} ${getX(i)} ${getY(v)}`).join(' ');
  return (
    <svg width={width} height={height}>
      <path d={`${path} L ${width} ${height} L 0 ${height} Z`} fill={color} fillOpacity={0.15} />
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" />
      <circle cx={getX(data.length - 1)} cy={getY(data[data.length - 1])} r={2} fill={color} />
    </svg>
  );
};
